import {
  isExpeditionCommandeActive,
  resolveExpeditionDisplayState,
} from '@clickorico/shared-domain/expeditionDisplayState';

/** Statuts Prisma « commande en cours » (hors terminées / annulées). */
export const STATUTS_COMMANDE_EN_COURS = [
  'EN_ATTENTE',
  'CONFIRMEE',
  'EN_PREPARATION',
  'PRETE',
  'EN_LIVRAISON',
];

/**
 * RDV qui doivent contribuer au badge « Mes Commandes ».
 * Exclut EN_ATTENTE_PAIEMENT (HOLD non payé), TERMINE, ANNULE, ABSENT.
 */
export const STATUTS_RDV_BADGE_EN_COURS = ['EN_ATTENTE', 'CONFIRME'];

/** Inclut CONFIRME (rendez-vous) pour les listes mélangées commandes + RDV. */
export const STATUTS_AFFICHAGE_EN_COURS = [...STATUTS_COMMANDE_EN_COURS, 'CONFIRME'];

export function isCommandeEnCours(statut) {
  return STATUTS_AFFICHAGE_EN_COURS.includes(String(statut || '').toUpperCase());
}

function expeditionDisplayForCommande(commande) {
  if (!commande || commande.modeRetrait !== 'expedition') return null;
  return resolveExpeditionDisplayState({
    commandeStatus: commande.statut,
    shipmentStatus: commande.shipment?.status,
    carrier: commande.shipment?.carrier,
    trackingNumber: commande.shipment?.trackingNumber,
    trackingUrl: commande.shipment?.trackingUrl,
    audience: 'merchant',
  });
}

/** Pipeline commerçant — expédition dérivée du Shipment quand présent. */
export function isMerchantCommandeEnCours(commande) {
  if (commande?.modeRetrait === 'expedition' && commande?.shipment?.status) {
    const active = isExpeditionCommandeActive(commande);
    if (active === false) return false;
    if (active === true) return true;
  }
  return isCommandeEnCours(commande?.statut);
}

export function isMerchantCommandeTerminee(commande) {
  const expedition = expeditionDisplayForCommande(commande);
  if (expedition) {
    return expedition.pipelineBucket === 'terminee';
  }
  const statut = String(commande?.statut || '').toUpperCase();
  return statut === 'LIVREE' || statut === 'RETIREE' || statut === 'TERMINE';
}

export function merchantExpeditionStatusLabel(commande) {
  const expedition = expeditionDisplayForCommande(commande);
  return expedition?.label || null;
}

export function isRdvBadgeEnCours(statut) {
  return STATUTS_RDV_BADGE_EN_COURS.includes(String(statut || '').toUpperCase());
}

/**
 * Plan de requêtes séparées (COMMANDE puis RENDEZ_VOUS) pour éviter le double compte.
 * @param {{ commercantId?: string|null }} [opts]
 * @returns {Array<{ type: string, statut: string, page: number, pageSize: number, excludeCanalVente?: string, commercantId?: string }>}
 */
export function buildPendingCountQueries({ commercantId = null } = {}) {
  const shared = { page: 1, pageSize: 1 };
  if (commercantId) {
    shared.commercantId = commercantId;
  }

  const commandeQueries = STATUTS_COMMANDE_EN_COURS.map((statut) => ({
    ...shared,
    type: 'COMMANDE',
    statut,
    excludeCanalVente: 'POS',
  }));

  const rdvQueries = STATUTS_RDV_BADGE_EN_COURS.map((statut) => ({
    ...shared,
    type: 'RENDEZ_VOUS',
    statut,
  }));

  return [...commandeQueries, ...rdvQueries];
}

/**
 * @param {Array<{ total?: number|string|null }|null|undefined>} results
 * @returns {number}
 */
export function sumPendingCountTotals(results = []) {
  return results.reduce((sum, data) => sum + (Number(data?.total) || 0), 0);
}

/**
 * Sépare et somme les totaux produit vs RDV sans double comptage.
 * @param {{ productTotals?: number[], rdvTotals?: number[] }} parts
 */
export function combinePendingCounts({ productTotals = [], rdvTotals = [] } = {}) {
  const productPendingCount = productTotals.reduce((s, n) => s + (Number(n) || 0), 0);
  const rdvPendingCount = rdvTotals.reduce((s, n) => s + (Number(n) || 0), 0);
  return {
    productPendingCount,
    rdvPendingCount,
    pendingCount: productPendingCount + rdvPendingCount,
  };
}

/** Commandes dont l'assignation livreur ne peut plus être modifiée. */
export const STATUTS_COMMANDE_LIVREUR_FIGE = ['LIVREE', 'ANNULEE'];

export function isCommandeLivreurModifiable(statut) {
  return !STATUTS_COMMANDE_LIVREUR_FIGE.includes(String(statut || '').toUpperCase());
}

export function formatLivreurNom(livreur) {
  if (!livreur) return null;
  const nomComplet = `${livreur.prenom || ''} ${livreur.nom || ''}`.trim();
  return nomComplet || livreur.telephone || null;
}
